import { TRelatorioAusencias } from '@acme/core'
import dayjs from 'dayjs'
import ApiService from '../api.service'
import { Abono } from './abono'
import { Afastamento } from './afastamentos'
import { Empresa } from './empresas'
import { CompensacaoFeriado } from './feriados'
import { Funcionario } from './funcionario'
import { Registro } from './registros'
import { Usuario } from './usuarios'

type AdditionalInfo = {
    MotivoExclusao?: { motivo: string }
}

type TResponse = {
    registros: (Registro & AdditionalInfo)[]
    abonos: (Abono & AdditionalInfo)[]
    afastamentos: (Afastamento & AdditionalInfo)[]
}

const TipoAjuste = ['REGISTRO', 'AFASTAMENTO', 'ABONO'] as const
export type TipoAjuste = (typeof TipoAjuste)[number]

export type Ajuste = {
    tipoAjuste: TipoAjuste
    id: `${TipoAjuste}_${number}`
    data: string
    Funcionario: Funcionario
    hora?: string | undefined
    justificativa?: string
    fileUrl?: string
    statusAprovacao: string
    origem: number
    Criador?: Usuario
}

type TAjuste = (Registro | Abono | Afastamento) & {
    tipo: Ajuste['tipoAjuste']
}

export type TImpressos = {
    id: number
    hash: string
    empresaId: number
    arquivo: string
    competencia: string
    request: {
        Afastamento: number[]
        Registro: number[]
        Abono: number[]
    }
    createdAt: Date
    updatedAt: Date
    assinadoEm?: Date
    Empresa: Empresa
    Funcionario: Funcionario
}

type TAjustesAprovados = {
    afastamentos: (Afastamento & {
        Funcionario: Funcionario
    })[]
    abonos: (Abono & {
        Funcionario: Funcionario
    })[]
    compensacaoFeriados: (CompensacaoFeriado & {
        Funcionario: Funcionario
    })[]
}

const service = new ApiService<TResponse>('ajustes')

export const ajustes = Object.assign(service, {
    aprovacao: new ApiService<TAjuste>('ajustes/aprovacao'),
    reprovacao: new ApiService<TAjuste>('ajustes/reprovacao'),
    relatorio: new ApiService<TResponse>('ajustes/relatorio'),
    impressos: new ApiService<TImpressos>('ajustes/impressos'),
    aprovados: new ApiService<
        any,
        TAjustesAprovados,
        {
            empresaId: number
            dataInicial: string
            dataFinal: string
        }
    >('ajustes/aprovados'),
    relatorioAusencias: new ApiService<TRelatorioAusencias>(
        'ajustes/relatorio-ausencias',
    ),
})

export class Ajustes {
    private readonly _data?: TResponse
    private readonly _ajustes: Ajuste[] = []

    constructor(data?: TResponse) {
        this._data = data
        this._ajustes = this.toAjustesArray()
    }

    get data() {
        return this._data
    }

    get ajustes() {
        return this._ajustes
    }

    private toAjustesArray(): Ajuste[] {
        if (!this.data) return []
        const abnt = (data: any) => dayjs.utc(data).format('DD/MM/YYYY')

        return [
            ...(this.data?.registros.map(
                item =>
                    ({
                        ...item,
                        tipoAjuste: 'REGISTRO',
                        id: `REGISTRO_${item.id}`,
                        data: abnt(item.data),
                        Funcionario: (item as any).Funcionario,
                        justificativa:
                            item?.MotivoExclusao?.motivo ?? item.justificativa,
                    }) as Ajuste,
            ) ?? []),

            ...(this.data?.abonos.map(
                item =>
                    ({
                        ...item,
                        tipoAjuste: 'ABONO',
                        id: `ABONO_${item.id}`,
                        data: abnt(item.data),
                        Funcionario: (item as any).Funcionario,
                        hora: item.quantidadeHoras,
                        justificativa:
                            item?.MotivoExclusao?.motivo ?? item.justificativa,
                    }) as Ajuste,
            ) ?? []),

            ...(this.data?.afastamentos.map(
                item =>
                    ({
                        ...item,
                        tipoAjuste: 'AFASTAMENTO',
                        id: `AFASTAMENTO_${item.id}`,
                        data: `${abnt(item.dataInicial)} - ${abnt(
                            item.dataFinal,
                        )}`,
                        Funcionario: (item as any).Funcionario,
                        hora: undefined,
                        justificativa:
                            item?.MotivoExclusao?.motivo ?? item.justificativa,
                    }) as Ajuste,
            ) ?? []),
        ].filter(Boolean)
    }
}
